import axios from 'axios';
import { getLocale, translateDeep } from '../i18n/translateService';

const PRODUCTION_API_URL = 'https://api.qtechcontracting.com';

export const getApiUrl = () => {
  const configured = process.env.REACT_APP_API_URL?.replace(/\/$/, '');
  if (typeof window !== 'undefined') {
    const { hostname } = window.location;
    const onProductionSite = hostname !== 'localhost' && hostname !== '127.0.0.1';
    if (onProductionSite && (!configured || configured.includes('localhost'))) {
      return PRODUCTION_API_URL;
    }
  }
  return configured || 'http://localhost:4000';
};

export const API_URL = getApiUrl();

const api = axios.create({
  baseURL: `${API_URL}/api`,
  timeout: 90000,
});

api.interceptors.request.use((config) => {
  const method = config.method?.toLowerCase();
  if (method && ['post', 'put', 'patch'].includes(method)) {
    config.headers['Content-Type'] = config.headers['Content-Type'] || 'application/json';
  } else if (config.headers) {
    delete config.headers['Content-Type'];
  }
  return config;
});

const localize = async (payload) => {
  if (getLocale() === 'en' || payload == null) return payload;
  return translateDeep(payload);
};

const unwrapList = (data) => (Array.isArray(data?.data) ? data.data : []);

export const getProjects = async () => {
  const { data } = await api.get('/projects');
  return localize(unwrapList(data));
};

export const getProjectBySlug = async (slug) => {
  const { data } = await api.get('/projects', {
    params: { 'filters[slug][$eq]': slug },
  });
  const items = unwrapList(data);
  return localize(items[0] || null);
};

export const getBlogs = async () => {
  const { data } = await api.get('/blogs', { params: { status: 'published' } });
  return localize(unwrapList(data));
};

export const getBlogBySlug = async (slug) => {
  const { data } = await api.get('/blogs', {
    params: { status: 'published', 'filters[slug][$eq]': slug },
  });
  const items = unwrapList(data);
  return localize(items[0] || null);
};

export const getServices = async () => {
  const { data } = await api.get('/services');
  return localize(unwrapList(data));
};

export const getServiceBySlug = async (slug) => {
  const { data } = await api.get('/services', {
    params: { 'filters[slug][$eq]': slug },
  });
  const items = unwrapList(data);
  return localize(items[0] || null);
};

export const getClients = async () => {
  const { data } = await api.get('/clients');
  return localize(unwrapList(data));
};

export const getGalleries = async () => {
  const { data } = await api.get('/galleries');
  return localize(unwrapList(data));
};

export const getReviews = async () => {
  const { data } = await api.get('/testimonials', { params: { status: 'published' } });
  return localize(unwrapList(data));
};

export const getTeamMembers = async () => {
  const { data } = await api.get('/team-members', { params: { status: 'published' } });
  return localize(unwrapList(data));
};

export const submitContactInquiry = async ({
  name,
  email,
  phone,
  serviceInquiry,
  message,
  sourcePage,
}) => {
  const { data } = await api.post('/contact-inquiries', {
    data: {
      name,
      email,
      phone: phone || null,
      serviceInquiry,
      message,
      sourcePage: sourcePage || 'website',
    },
  });
  return data;
};

export const subscribeNewsletter = async (email, sourcePage = 'footer') => {
  const { data } = await api.post('/newsletter-subscribers', {
    data: { email, sourcePage },
  });
  return data;
};

export default api;
